import React from 'react';
import { useApp } from '../context/PetParksContext';
import { X } from 'lucide-react';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Badge } from './ui/badge';

interface FilterPanelProps {
  onClose: () => void;
}

export const FilterPanel: React.FC<FilterPanelProps> = ({ onClose }) => {
  const { filters, setFilters, clearFilters, activeFilterCount, t } = useApp();

  const handleFilterChange = (key: keyof typeof filters, value: boolean) => {
    setFilters({ ...filters, [key]: value });
  };

  const FilterSection: React.FC<{
    title: string;
    children: React.ReactNode;
  }> = ({ title, children }) => (
    <div className="space-y-3">
      <h3 className="font-semibold text-foreground">{title}</h3>
      <div className="space-y-2.5">{children}</div>
    </div>
  );

  const FilterCheckbox: React.FC<{
    id: keyof typeof filters;
    label: string;
  }> = ({ id, label }) => (
    <div className="flex items-center gap-2 min-w-0">
      <Checkbox
        id={id}
        checked={filters[id]}
        onCheckedChange={(checked) =>
          handleFilterChange(id, checked as boolean)
        }
        className="shrink-0"
      />
      <Label
        htmlFor={id}
        className="cursor-pointer text-sm font-normal leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 break-words min-w-0"
      >
        {label}
      </Label>
    </div>
  );

  return (
    <div className="flex h-full flex-col bg-card min-w-0 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between border-b p-3 sm:p-4 min-w-0 shrink-0">
        <div className="flex items-center gap-2">
          <h2 className="text-base font-semibold sm:text-lg">{t('filters')}</h2>
          {activeFilterCount > 0 && (
            <Badge
              variant="default"
              className="h-5 min-w-5 rounded-full px-1.5"
            >
              {activeFilterCount}
            </Badge>
          )}
        </div>
        <Button
          variant="ghost"
          size="icon"
          onClick={onClose}
          className="h-9 w-9 sm:h-8 sm:w-8 cursor-pointer"
        >
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Filters */}
      <ScrollArea className="flex-1">
        <div className="space-y-5 p-3 sm:space-y-6 sm:p-4">
          {/* Access Rules */}
          <FilterSection title={t('accessRules')}>
            <FilterCheckbox id="offLeash" label={t('offLeash')} />
            <FilterCheckbox id="onLeash" label={t('onLeash')} />
            <FilterCheckbox id="mixedZones" label={t('mixedZones')} />
          </FilterSection>

          <Separator />

          {/* Safety & Layout */}
          <FilterSection title={t('safetyLayout')}>
            <FilterCheckbox id="fullyFenced" label={t('fullyFenced')} />
            <FilterCheckbox id="partiallyFenced" label={t('partiallyFenced')} />
            <FilterCheckbox id="noFencing" label={t('noFencing')} />
            <FilterCheckbox id="separateAreas" label={t('separateAreas')} />
          </FilterSection>

          <Separator />

          {/* Amenities */}
          <FilterSection title={t('amenities')}>
            <FilterCheckbox id="waterFountain" label={t('waterFountain')} />
            <FilterCheckbox id="wasteBags" label={t('wasteBags')} />
            <FilterCheckbox id="benches" label={t('benches')} />
            <FilterCheckbox id="shade" label={t('shade')} />
            <FilterCheckbox id="parking" label={t('parking')} />
            <FilterCheckbox
              id="agilityEquipment"
              label={t('agilityEquipment')}
            />
          </FilterSection>

          <Separator />

          {/* Environment */}
          <FilterSection title={t('environment')}>
            <div className="space-y-1.5">
              <p className="text-sm text-muted-foreground">{t('surface')}</p>
              <div className="space-y-2.5 pl-1">
                <FilterCheckbox id="grass" label={t('grass')} />
                <FilterCheckbox id="dirt" label={t('dirt')} />
                <FilterCheckbox
                  id="artificialTurf"
                  label={t('artificialTurf')}
                />
              </div>
            </div>

            <div className="space-y-1.5 pt-2">
              <p className="text-sm text-muted-foreground">{t('terrain')}</p>
              <div className="space-y-2.5 pl-1">
                <FilterCheckbox id="flatTerrain" label={t('flatTerrain')} />
                <FilterCheckbox id="hillyTerrain" label={t('hillyTerrain')} />
              </div>
            </div>

            <div className="space-y-1.5 pt-2">
              <p className="text-sm text-muted-foreground">{t('crowdLevel')}</p>
              <div className="space-y-2.5 pl-1">
                <FilterCheckbox id="quiet" label={t('quiet')} />
                <FilterCheckbox id="crowded" label={t('crowded')} />
              </div>
            </div>
          </FilterSection>
        </div>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t p-4">
        <div className="flex gap-2">
          <Button
            variant="outline"
            className="flex-1 cursor-pointer"
            onClick={clearFilters}
            disabled={activeFilterCount === 0}
          >
            {t('clearFilters')}
          </Button>
          <Button className="flex-1 cursor-pointer" onClick={onClose}>
            {t('applyFilters')}
          </Button>
        </div>
      </div>
    </div>
  );
};
